import { useState, useEffect } from "react";
import {
  Receipt, Plus, Edit2, Trash2, X, RefreshCw,
  ToggleLeft, ToggleRight, Info,
} from "lucide-react";
import { useAdmin } from "../admin-context";

const APPLIES_TO_LABEL = { all: "All Orders", delivery: "Delivery", dine_in: "Dine-in", takeaway: "Takeaway" };
const APPLIES_OPTIONS = ["all", "delivery", "dine_in", "takeaway"];

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const inp = "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300";
const sel = `${inp} bg-white`;
const EMPTY = { name: "", rate: "", applies_to: "all", is_inclusive: false, status: "active" };

const TaxRulesPage = () => {
  const { taxRules, loadTaxRules, createTaxRule, updateTaxRule, deleteTaxRule, showToast } = useAdmin();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [previewAmount, setPreviewAmount] = useState("100");

  useEffect(() => { loadTaxRules(); }, []);

  const F = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openAdd  = () => { setEditing(null); setForm(EMPTY); setDrawerOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setDrawerOpen(true); };

  const handleSave = async () => {
    if (!form.name || form.rate === "") return showToast("Name and rate are required", "error");
    setSaving(true);
    if (editing) await updateTaxRule(editing.id, { ...form, rate: Number(form.rate), is_inclusive: !!form.is_inclusive });
    else         await createTaxRule({ ...form, rate: Number(form.rate), is_inclusive: !!form.is_inclusive });
    setSaving(false);
    setDrawerOpen(false);
    showToast(editing ? "Tax rule updated" : "Tax rule created");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tax rule?")) return;
    await deleteTaxRule(id);
    showToast("Deleted");
  };

  const handleToggle = async (rule) => {
    await updateTaxRule(rule.id, { status: rule.status === "active" ? "inactive" : "active" });
    showToast("Updated");
  };

  const rules = taxRules || [];
  const activeRules = rules.filter((r) => r.status === "active");
  const previewAmt  = parseFloat(previewAmount) || 0;
  const preview     = activeRules.map((r) => ({
    name:   r.name,
    rate:   Number(r.rate),
    amount: Number((previewAmt * Number(r.rate) / 100).toFixed(2)),
    inclusive: r.is_inclusive,
  }));
  const totalTax   = preview.filter((p) => !p.inclusive).reduce((s, p) => s + p.amount, 0);
  const totalFinal = previewAmt + totalTax;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Tax Rules</h1>
          <p className="text-sm text-gray-500 mt-0.5">Configure taxes applied to orders at checkout</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadTaxRules()} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw size={15} /> Refresh
          </button>
          <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600">
            <Plus size={16} /> Add Rule
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Rules list */}
        <div className="lg:col-span-2 space-y-3">
          {rules.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
              <Receipt size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No tax rules defined yet</p>
              <p className="text-sm mt-1">Add your first rule to apply taxes at checkout</p>
            </div>
          ) : rules.map((rule) => (
            <div key={rule.id} className={`bg-white rounded-xl border p-4 flex items-center justify-between gap-4 transition ${rule.status === "inactive" ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${rule.status === "active" ? "bg-orange-100" : "bg-gray-100"}`}>
                  <Receipt size={18} className={rule.status === "active" ? "text-orange-500" : "text-gray-400"} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-gray-900">{rule.name}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {APPLIES_TO_LABEL[rule.applies_to] || rule.applies_to}
                    </span>
                    {rule.is_inclusive && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Inclusive</span>
                    )}
                  </div>
                  <p className="text-2xl font-extrabold text-orange-500">{Number(rule.rate).toFixed(2)}%</p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => handleToggle(rule)} title={rule.status === "active" ? "Disable" : "Enable"}
                  className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500">
                  {rule.status === "active" ? <ToggleRight size={18} className="text-green-500" /> : <ToggleLeft size={18} />}
                </button>
                <button onClick={() => openEdit(rule)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Edit2 size={14} /></button>
                <button onClick={() => handleDelete(rule.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Tax preview calculator */}
        <div className="bg-white rounded-xl border p-5 space-y-4 h-fit">
          <div className="flex items-center gap-2">
            <Info size={16} className="text-orange-500" />
            <p className="font-bold text-gray-900">Tax Preview</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Order amount ($)</label>
            <input type="number" min="0" value={previewAmount} onChange={(e) => setPreviewAmount(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
          </div>
          {activeRules.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No active tax rules</p>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span className="font-medium">${previewAmt.toFixed(2)}</span>
              </div>
              {preview.map((p, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-gray-500">{p.name} ({p.rate}%){p.inclusive ? " incl." : ""}</span>
                  <span className="font-medium text-orange-600">+${p.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-2 flex justify-between font-bold text-gray-900">
                <span>Total</span>
                <span>${totalFinal.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-400 text-center">Tax: ${totalTax.toFixed(2)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Edit Tax Rule" : "Add Tax Rule"}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name <span className="text-red-500">*</span></label>
            <input value={form.name} onChange={F("name")} placeholder="e.g. VAT, GST, Service Tax" className={inp} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate (%) <span className="text-red-500">*</span></label>
            <input type="number" min="0" max="100" step="0.01" value={form.rate} onChange={F("rate")} placeholder="e.g. 8.50" className={inp} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Applies To</label>
            <select value={form.applies_to} onChange={F("applies_to")} className={sel}>
              {APPLIES_OPTIONS.map((o) => (
                <option key={o} value={o}>{APPLIES_TO_LABEL[o]}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <input type="checkbox" id="inclusive" checked={!!form.is_inclusive}
              onChange={(e) => setForm((f) => ({ ...f, is_inclusive: e.target.checked }))}
              className="w-4 h-4 accent-orange-500" />
            <label htmlFor="inclusive" className="text-sm text-gray-700">
              <span className="font-medium">Tax-inclusive pricing</span>
              <span className="text-gray-400 text-xs block">Price already includes this tax (display only)</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={F("status")} className={sel}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition disabled:opacity-60">
            {saving ? "Saving..." : editing ? "Update Rule" : "Create Rule"}
          </button>
        </div>
      </Drawer>
    </div>
  );
};

export default TaxRulesPage;
